import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { copyText } from '@/lib/clipboard';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';

/**
 * Código que se clica para copiar (SKU, GTIN): quem trabalha o alerta cola o código no ERP ou na
 * plataforma, e selecionar texto miúdo com o mouse erra. O ícone confirma no lugar; o aviso diz o
 * quê foi copiado, para quem não olhou para o ícone.
 *
 * O ícone só aparece com o mouse em cima (ou foco pelo teclado) e logo depois de copiar: numa
 * lista, um ícone por linha polui (pedido do dono). Sem mouse, o toque copia do mesmo jeito.
 */
export function CopyValue({
  value,
  label,
  className,
}: {
  value: string;
  /** O que é o valor, para o aviso e o leitor de tela: "SKU", "GTIN". */
  label: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const Icon = copied ? Check : Copy;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      title={`Copiar ${label}`}
      aria-label={`Copiar ${label} ${value}`}
      onClick={() =>
        void copyText(value).then((ok) => {
          if (!ok) {
            toast.error(`Não foi possível copiar; selecione o ${label} e copie.`);
            return;
          }
          setCopied(true);
          toast.success(`${label} copiado: ${value}`);
        })
      }
      className={cn('group h-auto gap-1 px-1 py-0 font-mono text-xs font-normal', className)}
    >
      {value}
      <Icon
        aria-hidden
        className={
          copied
            ? 'text-success-ink'
            : 'text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100'
        }
      />
    </Button>
  );
}
